import {
  getDiscoveryPlacementsForWorkerQuerySchema,
  getDiscoveryPlacementsForWorkerResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { type z } from "zod";

// Use z.output to match what parsedApi.get() returns — parsed and transformed response data.
export type DiscoveryPlacementsResponse = z.output<
  typeof getDiscoveryPlacementsForWorkerResponseSchema
>;

export function fetchDiscoveryPlacementsApiUrl(workerId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/v2/workers/${workerId}/placements`;
}

interface FetchDiscoveryPlacementsParams {
  [key: string]: unknown;
  workerId: string;
  placementCandidateId: string;
  filter?: {
    [key: string]: unknown;
    workerTypes?: string;
    jobTypes?: string;
    shiftTypes?: string;
    daysOfWeek?: string;
    state?: string;
    tags?: string;
    matchedType?: string;
    newGradFriendly?: boolean;
    workplaceId?: string;
  };
  workerTypesAvailableForHiring?: string[];
  shouldLogEvent?: boolean;
}

export function mapFilterToDiscoveryQuery(
  filter: FetchDiscoveryPlacementsParams["filter"]
): Record<string, unknown> {
  if (!filter) {
    return {};
  }

  const mapped: Record<string, unknown> = {};

  // useGetPlacementFilters outputs distance as "distanceInMiles.$lt": number (flat key).
  // Map it to the discovery API's distanceInMiles (flat number).
  const distanceValue = filter["distanceInMiles.$lt"];
  if (isDefined(distanceValue)) {
    mapped.distanceInMiles = distanceValue;
  }

  if (isDefined(filter.workerTypes)) {
    mapped.workerTypes = filter.workerTypes;
  }
  if (isDefined(filter.jobTypes)) {
    mapped.jobTypes = filter.jobTypes;
  }
  if (isDefined(filter.shiftTypes)) {
    mapped.shiftTypes = filter.shiftTypes;
  }
  if (isDefined(filter.daysOfWeek)) {
    mapped.daysOfWeek = filter.daysOfWeek;
  }
  if (isDefined(filter.state)) {
    mapped.state = filter.state;
  }
  if (isDefined(filter.tags)) {
    mapped.tags = filter.tags;
  }
  if (isDefined(filter.matchedType)) {
    mapped.matchedType = filter.matchedType;
  }
  if (isDefined(filter.newGradFriendly)) {
    mapped.newGradFriendly = filter.newGradFriendly;
  }
  if (isDefined(filter.workplaceId)) {
    mapped.workplaceIds = [filter.workplaceId];
  }

  return mapped;
}

export function useFetchDiscoveryPlacements(
  params: FetchDiscoveryPlacementsParams,
  options: UseInfiniteQueryOptions<DiscoveryPlacementsResponse, AxiosError> = {}
) {
  const {
    workerId,
    placementCandidateId,
    filter,
    workerTypesAvailableForHiring,
    shouldLogEvent = true,
  } = params;
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const source = searchParams.get("source");

  const nonEmptyFilter = useMemo(() => {
    const mappedFilter = mapFilterToDiscoveryQuery(filter);
    return Object.fromEntries(
      Object.entries(mappedFilter).filter(([_, value]) => isDefined(value))
    );
  }, [filter]);

  // Fire analytics from an effect (not the queryFn) so the event isn't
  // suppressed on cache hits. queryFn only runs on a network fetch, which
  // means shared cache entries across routes would silently skip the event.
  const isEnabled = options.enabled !== false;
  // Key by a stringified filter so unstable object refs from callers don't
  // refire the event on every render. workerTypesAvailableForHiring is
  // intentionally NOT a view boundary — it's analytics metadata that may
  // arrive async, so triggering on it would double-count a single view.
  const filterKey = JSON.stringify(nonEmptyFilter);
  const filterRef = useRef(nonEmptyFilter);
  filterRef.current = nonEmptyFilter;
  const workerTypesRef = useRef(workerTypesAvailableForHiring);
  workerTypesRef.current = workerTypesAvailableForHiring;
  useEffect(() => {
    if (!isEnabled || !shouldLogEvent || isEmpty(placementCandidateId)) {
      return;
    }

    const workerTypes = workerTypesRef.current;
    logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_VIEWED, {
      placementCandidateId,
      filter: filterRef.current,
      source,
      ...(isDefined(workerTypes) &&
        workerTypes.length > 0 && {
          workerTypesAvailableForHiring: workerTypes.join(","),
        }),
    });
  }, [isEnabled, shouldLogEvent, placementCandidateId, filterKey, source]);

  // `source` intentionally NOT in the queryKey — it's a URL-derived analytics
  // tag, not a filter that affects what data comes back. Including it would
  // fragment the cache across routes (e.g. the workplace profile's button and
  // the destination's list carry different `source` values but want the same
  // data).
  return useInfiniteQuery({
    queryKey: [fetchDiscoveryPlacementsApiUrl(workerId), filter],
    keepPreviousData: true,
    queryFn: async ({
      pageParam: pageParameter,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const response = await get({
        url: fetchDiscoveryPlacementsApiUrl(workerId),
        queryParamsSchema: getDiscoveryPlacementsForWorkerQuerySchema,
        queryParams: {
          page: {
            size: !isDefined(pageParameter) ? 5 : 15,
            cursor: pageParameter,
          },
          filter: nonEmptyFilter,
        },
        responseSchema: getDiscoveryPlacementsForWorkerResponseSchema,
      });

      // Client-side sort: prioritize placements with valid document requirements
      const sortedData = response.data.data.toSorted((a, b) => {
        if (a.meta.isDocumentRequirementValid && !b.meta.isDocumentRequirementValid) {
          return -1;
        }

        if (!a.meta.isDocumentRequirementValid && b.meta.isDocumentRequirementValid) {
          return 1;
        }

        return 0;
      });

      return { ...response.data, data: sortedData };
    },
    getNextPageParam: (lastPage) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}
